import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Button, Stack, Paper, Chip, Avatar, Link } from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Pinterest as PinterestIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Google as GoogleIcon,
  Language as WebIcon,
  AlternateEmail as ThreadsIcon,
  MusicNote as TikTokIcon,
  Cloud as BlueskyIcon,
  EditNote as ComposerIcon,
  CalendarMonth as CalendarIcon,
  PhotoLibrary as MediaIcon,
  AutoAwesome as AIIcon,
  Groups as TeamIcon,
  Insights as InsightsIcon,
  Article as BlogIcon,
  Forum as InboxIcon,
  Lock as LockIcon,
  VerifiedUser as VerifiedIcon,
  DeleteSweep as DeleteIcon,
  Link as ConnectIcon,
  Send as SendIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

import { FaQuora } from 'react-icons/fa6';

import PublicLayout, { SITE_NAME, CONTACT_EMAIL } from '../../components/Layout/PublicLayout';

// Public landing page at "/" for logged-out visitors (logged-in users get
// the Dashboard at "/"). Feature copy describes what the app actually does
// today — keep it in step when features change.
const PLATFORMS = [
  { name: 'Facebook', icon: <FacebookIcon />, color: '#1877F2' },
  { name: 'Instagram', icon: <InstagramIcon />, color: '#E4405F' },
  { name: 'YouTube', icon: <YouTubeIcon />, color: '#FF0000' },
  { name: 'Pinterest', icon: <PinterestIcon />, color: '#E60023' },
  { name: 'X (Twitter)', icon: <TwitterIcon />, color: '#14171A' },
  { name: 'LinkedIn', icon: <LinkedInIcon />, color: '#0A66C2' },
  { name: 'Threads', icon: <ThreadsIcon />, color: '#101010' },
  { name: 'TikTok', icon: <TikTokIcon />, color: '#111111' },
  { name: 'Google Business', icon: <GoogleIcon />, color: '#4285F4' },
  { name: 'WordPress', icon: <WebIcon />, color: '#21759B' },
  { name: 'Bluesky', icon: <BlueskyIcon />, color: '#1185FE' },
  { name: 'Quora', icon: <FaQuora />, color: '#B92B27' },
];

const FEATURES = [
  { icon: <ComposerIcon />, title: 'One composer, every platform', text: 'Write once, tailor the text per platform, and see a live preview of exactly how each post will look before it goes out.' },
  { icon: <CalendarIcon />, title: 'Schedule and calendar', text: 'Plan weeks ahead on a calendar, schedule posts for the best time, or publish instantly with one click.' },
  { icon: <MediaIcon />, title: 'Media library', text: 'Keep every brand\'s images and videos in one place and attach them to any post in seconds.' },
  { icon: <AIIcon />, title: 'AI writing assistant', text: 'Get caption ideas, hashtags and full post drafts from AI, then edit them in your own voice.' },
  { icon: <BlogIcon />, title: 'Blog publishing', text: 'Write full articles and publish them straight to one or more connected WordPress sites.' },
  { icon: <TeamIcon />, title: 'Teams and approvals', text: 'Invite teammates with roles, and route posts through approval before they are published.' },
  { icon: <InboxIcon />, title: 'Unified inbox', text: 'Read and reply to comments from connected channels without switching between apps.' },
  { icon: <InsightsIcon />, title: 'Analytics and reports', text: 'Track what you published and how it performed, per brand and per platform.' },
];

const STEPS = [
  { icon: <ConnectIcon />, title: 'Connect your accounts', text: 'Sign in to each platform through its official login and choose the Pages, boards or channels to manage.' },
  { icon: <ComposerIcon />, title: 'Create and schedule', text: 'Write your post, add images or video, check the previews, and pick a time — or publish now.' },
  { icon: <SendIcon />, title: 'Publish everywhere', text: 'Posts go out through each platform\'s official API, and you can follow every result from one dashboard.' },
];

const TRUST = [
  { icon: <VerifiedIcon />, title: 'Official APIs only', text: 'We connect through each platform\'s approved login and publish only what you create or schedule.' },
  { icon: <LockIcon />, title: 'Encrypted credentials', text: 'Access tokens and app passwords are encrypted with AES-256-GCM; passwords are hashed.' },
  { icon: <DeleteIcon />, title: 'Your data, your call', text: 'Disconnect any account instantly, and ask us to delete everything at any time.' },
];

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 5, md: 7 } }}>
    <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.1em' }}>{eyebrow}</Typography>
    <Typography variant="h3" component="h2" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: '1.05rem', lineHeight: 1.7 }}>{subtitle}</Typography>
    )}
  </Box>
);

const IconBadge = ({ children }) => (
  <Box
    sx={{
      width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(79,70,229,0.1)', color: 'primary.main', flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

// A static, simplified picture of the composer — illustration only.
const HeroMockup = () => (
  <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 2.5, boxShadow: '0 24px 60px -24px rgba(20,22,31,0.25)' }}>
    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
      {PLATFORMS.slice(0, 4).map((p) => (
        <Chip
          key={p.name}
          size="small"
          icon={React.cloneElement(p.icon, { style: { color: p.color } })}
          label={p.name}
          variant="outlined"
        />
      ))}
    </Stack>
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
        New this week: our spring collection is here! Tap the link to see every piece. #NewArrivals #SpringStyle
      </Typography>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
      <Box sx={{ borderRadius: 2, height: 96, background: 'linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)' }} />
      <Box sx={{ borderRadius: 2, height: 96, background: 'linear-gradient(135deg, #F5B94D 0%, #D97F57 100%)' }} />
    </Box>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1} alignItems="center">
        <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">Scheduled · Fri 10:00 AM</Typography>
      </Stack>
      <Chip size="small" color="primary" label="Publish to 4 platforms" />
    </Stack>
  </Paper>
);

const Home = () => {
  const { hash } = useLocation();

  // Top-bar and footer links are "/#section" — scroll to it once rendered.
  useEffect(() => {
    document.title = `${SITE_NAME} — schedule and publish to every social platform`;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <PublicLayout>
      {/* Hero */}
      <Box sx={{ background: 'radial-gradient(ellipse at 20% 0%, rgba(129,140,248,0.18), transparent 60%)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Box sx={{ display: 'grid', gap: { xs: 6, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, alignItems: 'center' }}>
            <Box>
              <Chip label="Social media management for brands and agencies" size="small" sx={{ mb: 3, fontWeight: 600, backgroundColor: 'rgba(79,70,229,0.1)', color: 'primary.main' }} />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, fontSize: { xs: '2.3rem', sm: '3rem', md: '3.6rem' } }}>
                Every brand, every platform, one workspace.
              </Typography>
              <Typography variant="h6" component="p" color="text.secondary" sx={{ mt: 3, fontWeight: 400, lineHeight: 1.6, maxWidth: 540 }}>
                Create, schedule and publish to Facebook, Instagram, YouTube, Pinterest, X, LinkedIn and more — with live previews, an AI assistant and team approvals built in.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button component={RouterLink} to="/register" variant="contained" size="large" disableElevation endIcon={<ArrowIcon />}>
                  Get started
                </Button>
                <Button component={RouterLink} to="/login" variant="outlined" size="large">
                  Log in
                </Button>
              </Stack>
            </Box>
            <HeroMockup />
          </Box>
        </Container>
      </Box>

      {/* Platforms */}
      <Box id="platforms" sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: 72, borderTop: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="Platforms" title="Publish wherever your audience is" subtitle="Connect through each platform's official login. Choose the Pages, boards, channels and sites you want to manage." />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' } }}>
            {PLATFORMS.map((p) => (
              <Paper key={p.name} variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: p.color, '& svg': { fontSize: 18 } }}>{p.icon}</Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="Features" title="Everything you need to run social, in one place" subtitle="From the first draft to the published post, without juggling a dozen apps and logins." />
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
            {FEATURES.map((f) => (
              <Paper key={f.title} variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                <IconBadge>{f.icon}</IconBadge>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{f.text}</Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* How it works */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72, backgroundColor: 'background.paper', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="How it works" title="Up and running in three steps" />
          <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
            {STEPS.map((s, i) => (
              <Box key={s.title}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 800, fontSize: 16 }}>{i + 1}</Avatar>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>{s.title}</Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>{s.text}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Security & privacy */}
      <Box id="security" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Security and privacy"
            title="Built to be trusted with your accounts"
            subtitle="We follow the developer policies of every platform we connect to, and the EU/UK GDPR."
          />
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
            {TRUST.map((t) => (
              <Stack key={t.title} direction="row" spacing={2}>
                <IconBadge>{t.icon}</IconBadge>
                <Box>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{t.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{t.text}</Typography>
                </Box>
              </Stack>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 5 }}>
            Read our <Link component={RouterLink} to="/privacy">Privacy Policy</Link>,{' '}
            <Link component={RouterLink} to="/terms">Terms of Service</Link> and{' '}
            <Link component={RouterLink} to="/data-deletion">Data Deletion</Link> instructions.
          </Typography>
        </Container>
      </Box>

      {/* CTA + contact */}
      <Box id="contact" sx={{ pb: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              borderRadius: 4, px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 }, textAlign: 'center', color: '#FFFFFF',
              background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
            }}
          >
            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Ready to simplify your social media?
            </Typography>
            <Typography sx={{ mt: 2, opacity: 0.85, fontSize: '1.05rem', maxWidth: 560, mx: 'auto' }}>
              Create your workspace, connect your first platform and schedule your first post in minutes.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
              <Button component={RouterLink} to="/register" variant="contained" size="large" disableElevation sx={{ bgcolor: '#FFFFFF', color: '#3730A3', '&:hover': { bgcolor: '#EDEFF6' } }}>
                Create your account
              </Button>
              <Button href={`mailto:${CONTACT_EMAIL}`} variant="outlined" size="large" sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Contact us
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ mt: 3, opacity: 0.75 }}>
              Questions? Email <Link href={`mailto:${CONTACT_EMAIL}`} sx={{ color: '#FFFFFF', fontWeight: 600 }}>{CONTACT_EMAIL}</Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </PublicLayout>
  );
};

export default Home;
